import React, { useState, useEffect } from 'react';
import { X, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { frontendDb as db } from '../../lib/frontend-db';
import type { CustomSection } from '../../lib/frontend-db';
import CustomSectionEditor from '../CustomSectionEditor';

interface SortableSectionProps {
  section: CustomSection;
  onEdit: (section: CustomSection) => void;
  onDelete: (id: string) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({ section, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-md p-6 ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          {...attributes}
          {...listeners}
          className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'none' }}
        >
          <GripVertical size={20} />
        </div>
        <div className="flex-grow">
          <h3 className="text-lg font-semibold">{section.title}</h3>
          <p className="text-gray-600 mt-1">
            {section.content.substring(0, 100)}...
          </p>
          {section.image_url && (
            <img
              src={section.image_url}
              alt={section.title}
              className="mt-4 w-full h-48 object-cover rounded"
            />
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(section)}
            className="text-green-700 hover:text-green-800"
          >
            Bearbeiten
          </button>
          <button
            onClick={() => onDelete(section.id)}
            className="text-red-600 hover:text-red-700"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionsPage = () => {
  const [sections, setSections] = useState<CustomSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingSection, setEditingSection] = useState<CustomSection | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const loadedSections = await db.getCustomSections();
      setSections(loadedSections);
    } catch (error) {
      console.error('Error loading sections:', error);
      setError('Fehler beim Laden der Sektionen');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sections.findIndex((section) => section.id === active.id);
    const newIndex = sections.findIndex((section) => section.id === over.id);

    const newSections = arrayMove(sections, oldIndex, newIndex);

    // Update order_index for all items
    const updatedItems = newSections.map((item, index) => ({
      ...item,
      order_index: index
    }));

    setSections(updatedItems);

    // Update the order in the database
    try {
      for (const item of updatedItems) {
        await db.updateCustomSection(item.id, { order_index: item.order_index });
      }
    } catch (error) {
      console.error('Error updating section order:', error);
      setError('Fehler beim Aktualisieren der Reihenfolge');
      loadSections(); // Reload original order on error
    }
  };

  const handleSubmit = async (data: Partial<CustomSection>) => {
    try {
      if (editingSection) {
        try {
          console.log('📝 Updating custom section:', editingSection.id);
          await db.updateCustomSection(editingSection.id, data);
          console.log('✅ Custom section updated successfully');
        } catch (updateError) {
          console.error('❌ Error updating custom section:', updateError);
          setError('Fehler beim Aktualisieren der Sektion: ' + (updateError as Error).message);
          return;
        }
      } else {
        try {
          console.log('📝 Creating new custom section');
          await db.createCustomSection({
            ...data,
            order_index: sections.length
          } as any);
          console.log('✅ Custom section created successfully');
        } catch (createError) {
          console.error('❌ Error creating custom section:', createError);
          setError('Fehler beim Erstellen der Sektion: ' + (createError as Error).message);
          return;
        }
      }
      window.location.reload();
    } catch (error) {
      console.error('❌ Unexpected error saving section:', error);
      setError('Unerwarteter Fehler beim Speichern der Sektion: ' + (error as Error).message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie diese Sektion wirklich löschen?')) {
      return;
    }

    try {
      await db.deleteCustomSection(id);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting section:', error);
      setError('Fehler beim Löschen der Sektion');
    }
  };

  if (loading) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Sektionen</h2>
        <button
          onClick={() => {
            setEditingSection(null);
            setShowEditor(true);
          }}
          className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800"
        >
          Neue Sektion
        </button>
      </div>

      {showEditor && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {editingSection ? 'Sektion bearbeiten' : 'Neue Sektion erstellen'}
            </h3>
            <button
              onClick={() => {
                setShowEditor(false);
                setEditingSection(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
          <CustomSectionEditor
            section={editingSection}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowEditor(false);
              setEditingSection(null);
            }}
            onDelete={handleDelete}
          />
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={sections.map(section => section.id)} 
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {sections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                onEdit={(section) => {
                  setEditingSection(section);
                  setShowEditor(true);
                }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default SectionsPage;

